/**
 * UploadFileButton — a Button that opens the file picker for an instance file.
 *
 * Used twice on Manage Portfolio (Portfolio details, and the empty instance
 * list), so the hidden input, the `.xlsx`-only check and the "same file twice"
 * reset live here once instead of being copied.
 *
 * It does not show errors itself — where a message belongs differs per place —
 * it hands an accepted file to `onFile` and a refusal message to `onReject`.
 */
import { useRef } from 'react';

import { UploadIcon } from '@components/common/icons';
import { Button } from '@components/ui';

import { UPLOAD_ACCEPT, isAllowedUpload } from '../portfolioFormRules';

export const UPLOAD_REJECTED_MESSAGE = 'Only .xlsx files are allowed.';

export function UploadFileButton({
  variant = 'primary',
  icon = <UploadIcon />,
  className,
  disabled = false,
  /** Why it is off, shown on hover (or any hint). */
  title,
  onFile,
  onReject,
  children = 'Upload file',
}) {
  const input = useRef(null);

  const onChange = (e) => {
    const file = e.target.files?.[0];
    // Reset, so choosing the same file again still fires a change.
    e.target.value = '';
    if (!file) return;
    if (!isAllowedUpload(file)) {
      onReject?.(UPLOAD_REJECTED_MESSAGE);
      return;
    }
    onFile?.(file);
  };

  return (
    <>
      {/* The visible button opens this hidden native picker. */}
      <input
        ref={input}
        type="file"
        accept={UPLOAD_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={onChange}
      />
      <Button
        variant={variant}
        className={className}
        startIcon={icon}
        disabled={disabled}
        title={title}
        onClick={() => input.current?.click()}
      >
        {children}
      </Button>
    </>
  );
}

export default UploadFileButton;
